import OSS from 'ali-oss'

/**
 * 阿里云OSS配置 - 从环境变量读取
 */
const ossConfig = {
  region: process.env.VUE_APP_OSS_REGION,
  accessKeyId: process.env.VUE_APP_OSS_ACCESS_KEY_ID,
  accessKeySecret: process.env.VUE_APP_OSS_ACCESS_KEY_SECRET,
  bucket: process.env.VUE_APP_OSS_BUCKET
}

/**
 * 官网图片配置
 * 修改这里的配置即可调整图片列表和OSS路径
 */
export const HEADER_IMAGE_CONFIG = [
  { key: 'head1', label: '页眉1', ossPath: '/lingbanrenweb/image/', fileName: 'yemei1.png' },
  { key: 'head2', label: '页眉2', ossPath: '/lingbanrenweb/image/', fileName: 'yemei11.png' },
  { key: 'head3', label: '页眉3', ossPath: '/lingbanrenweb/image/', fileName: 'yemei22.png' },
  { key: 'head4', label: '页眉4', ossPath: '/lingbanrenweb/image/', fileName: 'yemei33.png' },
  { key: 'head5', label: '页眉5', ossPath: '/lingbanrenweb/image/', fileName: 'yemei44.png' }
]

/**
 * 案例图片OSS基础路径
 */
const CASE_OSS_BASE_PATH = '/lingbanrenweb/case/'

/**
 * 案例图片固定前缀名
 */
const CASE_IMAGE_PREFIX = 'image'

/**
 * 获取图片的完整OSS URL
 * @param {string} imageKey - 图片key
 * @returns {string} 完整的图片URL
 */
export function getImageUrl(imageKey) {
  const config = HEADER_IMAGE_CONFIG.find(item => item.key === imageKey)
  if (!config) return ''
  const baseUrl = process.env.VUE_APP_OSS_BASE_URL
  const fullOssPath = `${config.ossPath.replace(/\/$/, '')}/${config.fileName}`
  return `${baseUrl}${fullOssPath.startsWith('/') ? '' : '/'}${fullOssPath}`
}

/**
 * 上传图片到阿里云OSS（同名覆盖）
 * @param {string} imageKey - 图片key (head1 ~ head5)
 * @param {File} file - 要上传的文件对象
 * @param {Function} onProgress - 进度回调 function(percent)，percent为0-100的数字
 * @returns {Promise<string>} 上传成功后的图片URL（带时间戳防缓存）
 */
export async function uploadImageToOSS(imageKey, file, onProgress) {
  const config = HEADER_IMAGE_CONFIG.find(item => item.key === imageKey)
  if (!config) {
    throw new Error(`无效的图片key: ${imageKey}`)
  }

  // 检查OSS配置是否完整
  if (!ossConfig.region || !ossConfig.accessKeyId || !ossConfig.accessKeySecret || !ossConfig.bucket) {
    throw new Error('阿里云OSS配置不完整，请检查 .env 文件中的配置')
  }

  const client = new OSS(ossConfig)

  // 拼接最终上传路径：ossPath + fileName，如 /lingbanrenweb/image/yemei1.png
  const uploadPath = `${config.ossPath.replace(/\/$/, '')}/${config.fileName}`
  const cleanUploadPath = uploadPath.startsWith('/') ? uploadPath.substring(1) : uploadPath

  // 设置上传选项：同名覆盖 + 公共读权限 + 禁止CDN缓存 + 进度回调
  const options = {
    headers: {
      'x-oss-object-acl': 'public-read',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    },
    progress: function(percent) {
      // ali-oss 的 progress 回调，percent 为 0~1 的小数
      if (onProgress) {
        onProgress(Math.round(percent * 100))
      }
    }
  }

  // 执行上传（ali-oss 的 put 方法同名文件会自动覆盖）
  await client.put(cleanUploadPath, file, options)

  // 返回完整URL，追加时间戳参数防止浏览器缓存
  const baseUrl = process.env.VUE_APP_OSS_BASE_URL
  const fullOssPath = `${config.ossPath.replace(/\/$/, '')}/${config.fileName}`
  const fullUrl = `${baseUrl}${fullOssPath.startsWith('/') ? '' : '/'}${fullOssPath}`
  // 追加时间戳强制刷新CDN/浏览器缓存
  const cacheBustUrl = `${fullUrl}?t=${Date.now()}`

  return cacheBustUrl
}

/**
 * 获取案例图片的OSS目录路径
 * @param {string} caseTitle - 案例标题
 * @returns {string} OSS目录路径（不带前导/）
 */
function getCaseDirPath(caseTitle) {
  const dirPath = `${CASE_OSS_BASE_PATH}${caseTitle}/`
  return dirPath.startsWith('/') ? dirPath.substring(1) : dirPath
}

/**
 * 获取案例图片的完整URL
 * @param {string} caseTitle - 案例标题
 * @param {number} imageIndex - 图片序号（从1开始）
 * @param {string} ext - 文件扩展名（如 .jpg）
 * @returns {string} 完整图片URL（带时间戳防缓存）
 */
export function getCaseImageUrl(caseTitle, imageIndex, ext) {
  const baseUrl = process.env.VUE_APP_OSS_BASE_URL
  const dirPath = CASE_OSS_BASE_PATH.replace(/\/$/, '')
  const fileName = `${CASE_IMAGE_PREFIX}${imageIndex}${ext}`
  return `${baseUrl}${dirPath}/${caseTitle}/${fileName}?t=${Date.now()}`
}

/**
 * 上传案例图片到OSS（同名覆盖）
 * 图片存放路径：/lingbanrenweb/case/{案例标题}/image{序号}.{ext}
 * @param {string} caseTitle - 案例标题（用作OSS目录名）
 * @param {File} file - 要上传的文件对象
 * @param {number} imageIndex - 图片序号（从1开始）
 * @param {Function} onProgress - 进度回调 function(percent)
 * @returns {Promise<object>} 上传成功返回 { url, path, fileName }
 */
export async function uploadCaseImage(caseTitle, file, imageIndex, onProgress) {
  if (!ossConfig.region || !ossConfig.accessKeyId || !ossConfig.accessKeySecret || !ossConfig.bucket) {
    throw new Error('阿里云OSS配置不完整，请检查 .env 文件中的配置')
  }

  if (!caseTitle || !caseTitle.trim()) {
    throw new Error('案例标题不能为空')
  }

  const client = new OSS(ossConfig)

  // 获取文件扩展名
  const fileExt = file.name.substring(file.name.lastIndexOf('.'))
  const fileName = `${CASE_IMAGE_PREFIX}${imageIndex}${fileExt}`
  const dirPath = getCaseDirPath(caseTitle.trim())
  const uploadPath = `${dirPath}${fileName}`

  const options = {
    headers: {
      'x-oss-object-acl': 'public-read',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    },
    progress: function(percent) {
      if (onProgress) {
        onProgress(Math.round(percent * 100))
      }
    }
  }

  await client.put(uploadPath, file, options)

  const baseUrl = process.env.VUE_APP_OSS_BASE_URL
  const fullUrl = `${baseUrl}/${uploadPath}?t=${Date.now()}`

  return {
    url: fullUrl,
    path: `/${uploadPath}`,
    fileName: fileName
  }
}

/**
 * 删除案例在OSS上的整个目录（包含所有图片文件）
 * @param {string} caseTitle - 案例标题
 * @returns {Promise<boolean>} 是否删除成功
 */
export async function deleteCaseFolder(caseTitle) {
  if (!ossConfig.region || !ossConfig.accessKeyId || !ossConfig.accessKeySecret || !ossConfig.bucket) {
    throw new Error('阿里云OSS配置不完整，请检查 .env 文件中的配置')
  }

  if (!caseTitle || !caseTitle.trim()) {
    throw new Error('案例标题不能为空')
  }

  const client = new OSS(ossConfig)
  const dirPath = getCaseDirPath(caseTitle.trim())

  try {
    // 列出目录下所有文件
    let listResult = await client.list({
      prefix: dirPath,
      'max-keys': 100
    })

    const objects = listResult.objects || []

    if (objects.length === 0) {
      return true // 目录为空，视为删除成功
    }

    // 收集所有文件key
    const keys = objects.map(obj => obj.name)

    // 批量删除文件
    await client.deleteMulti(keys, { quiet: true })

    return true
  } catch (error) {
    console.error('删除OSS案例目录失败:', error)
    throw new Error('删除OSS案例图片失败: ' + (error.message || '未知错误'))
  }
}